import React, { useRef } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Loader } from "../../../../ui/components/Loader";
import {
  FeatherPaperclip,
  FeatherFile,
  FeatherX,
  FeatherAlertCircle,
  FeatherCheck,
  FeatherEye,
  FeatherDownload,
} from "@subframe/core";
import { ChecklistItemEntity } from "../../domain/entities/ChecklistEntity";

interface ProofUploadSectionProps {
  item: ChecklistItemEntity;
  onUpload: (file: File) => Promise<void>;
  onDelete?: (attachmentId: number) => Promise<void>;
  uploading?: boolean;
  deletingAttachmentId?: number | null;
  disabled?: boolean;
  /** When true (approved or sent back): hide Mark as Done/Upload, show Download only */
  readOnly?: boolean;
}

export const ProofUploadSection: React.FC<ProofUploadSectionProps> = ({
  item,
  onUpload,
  onDelete,
  uploading = false,
  deletingAttachmentId = null,
  disabled = false,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isProcessing = uploading || deletingAttachmentId !== null;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await onUpload(file);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    if (!disabled && !isProcessing) {
      fileInputRef.current?.click();
    }
  };

  const handleDelete = (e: React.MouseEvent, attachmentId: number) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(attachmentId);
    }
  };

  const hasAttachments = item.attachments && item.attachments.length > 0;
  const hasProof = hasAttachments;

  const handleDownload = (
    e: React.MouseEvent,
    url: string,
    filename?: string,
  ) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    if (filename) link.download = filename;
    link.click();
  };

  return (
    <div className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">
          Supporting Documentation
        </span>
        <div className="flex items-center gap-2">
          {hasProof ? (
            <>
              <FeatherCheck className="text-body font-body text-success-600" />
              <span className="text-caption-bold font-caption-bold text-success-600">
                Proof attached
              </span>
            </>
          ) : (
            <>
              <FeatherAlertCircle className="text-body font-body text-neutral-400" />
              <span className="text-caption font-caption text-neutral-400">
                Proof required
              </span>
            </>
          )}
        </div>
      </div>

      {/* Loader shown under Supporting Documentation during upload/delete */}
      {isProcessing && (
        <div className="flex w-full items-center gap-2 py-1">
          <Loader size="small" />
          <span className="text-caption font-caption text-subtext-color">
            {uploading ? "Uploading..." : "Deleting..."}
          </span>
        </div>
      )}

      {/* Hidden file input - only when not readOnly */}
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={disabled || isProcessing}
        />
      )}

      {/* Display uploaded attachments */}
      {hasAttachments &&
        item.attachments.map((attachment) => {
          const isDeleting = deletingAttachmentId === attachment.id;
          return (
            <div
              key={attachment.id}
              className="flex w-full items-center gap-4 rounded-md bg-neutral-50 px-3 py-3"
              onClick={(e) => e.stopPropagation()}
            >
              <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {attachment.original_filename || attachment.file_name}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Uploaded{" "}
                  {new Date(attachment.uploaded_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {attachment.s3_url && (
                  <>
                    <IconButton
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherEye />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(attachment.s3_url, "_blank");
                      }}
                      title="View attachment"
                      disabled={isDeleting}
                    />
                    <IconButton
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherDownload />}
                      onClick={(e) =>
                        handleDownload(
                          e,
                          attachment.s3_url!,
                          attachment.original_filename || attachment.file_name,
                        )
                      }
                      title="Download attachment"
                      disabled={isDeleting}
                    />
                  </>
                )}
                {!readOnly && !disabled && onDelete && (
                  <IconButton
                    variant="neutral-tertiary"
                    size="small"
                    icon={<FeatherX />}
                    onClick={(e) => handleDelete(e, attachment.id)}
                    title="Delete attachment"
                    disabled={isDeleting || isProcessing}
                  />
                )}
              </div>
            </div>
          );
        })}

      {/* Upload button - hidden in readOnly; existing attachments can be replaced by uploading another file. */}
      {readOnly ? (
        hasAttachments && (
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherDownload />}
            onClick={(e) => {
              e.stopPropagation();
              const first = item.attachments?.[0];
              if (first?.s3_url) {
                handleDownload(
                  e as unknown as React.MouseEvent,
                  first.s3_url,
                  first.original_filename || first.file_name,
                );
              }
            }}
          >
            Download
          </Button>
        )
      ) : (
        <Button
          variant="neutral-tertiary"
          size="small"
          icon={<FeatherPaperclip />}
          onClick={handleUploadClick}
          disabled={disabled || isProcessing}
        >
          {uploading ? "Uploading..." : "Upload File"}
        </Button>
      )}

      {!readOnly && !hasProof && (
        <div className="flex items-center gap-2">
          <FeatherAlertCircle className="text-body font-body text-neutral-400" />
          <span className="text-caption font-caption text-neutral-400">
            Proof required before marking as done
          </span>
        </div>
      )}
    </div>
  );
};
